/** TodoCreateModal.tsx — 일일 업무 신규 등록 모달 */
import { useState, useRef } from 'react';
import { C, T, R } from './ui';
import * as todosApi from '../api-todos';
import type { Staff } from './AssigneeChipPicker';
import AssigneeChipPicker from './AssigneeChipPicker';
import DueChipPicker from './DueChipPicker';
import RecurrencePicker, { type RecurType } from './RecurrencePicker';

interface Props {
  myId: string;
  staff: Staff[];
  onClose: () => void;
  onCreated: () => void;
  onToast?: (msg: string, kind?: 'success' | 'error') => void;
}

/** 누구에게 묻고 답을 전해야 하는 일 — 「최가연 팀장님 ○○ 문의」 · 「김대표님 확인」 (켜는 기본값만 정함 · 체크는 손으로 바꿈) */
const ASK_RE = /(팀장|지점장|본부장|사업단장|센터장|대표|설계사)님|문의|물어/;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
}

export default function TodoCreateModal({ myId, staff, onClose, onCreated, onToast }: Props) {
  const [title, setTitle] = useState('');
  const [desc, setDesc] = useState('');
  const [assignees, setAssignees] = useState<string[]>([myId]);
  const [due, setDue] = useState<string | null>(null);
  const [recurOn, setRecurOn] = useState(false);
  const [recurType, setRecurType] = useState<RecurType>('weekly');
  const [recurDays, setRecurDays] = useState('monday');
  const [files, setFiles] = useState<File[]>([]);
  // 「답 받고 내가 확인」 — 그냥 시키는 일도 있어 기본은 꺼 두고, 제목에 팀장님 · 대표님 같은 분이 있으면 켬(묻는 일) · 손으로 고르면 그대로
  //  (2026-10-22 지도사 「오재인이 누구에게 묻는 게 아니라 그냥 하라고 시키는 일도 있다」)
  const [checkPick, setCheckPick] = useState<boolean | null>(null);
  const needsCheck = checkPick ?? ASK_RE.test(title);
  const setNeedsCheck = (v: boolean) => setCheckPick(v);
  const toOther = assignees.some(a => a !== myId);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const canSave = title.trim().length > 0 && !busy
    && (!recurOn || recurType === 'daily' || (recurType === 'weekly' && recurDays.length > 0));

  function addFiles(list: FileList | null) {
    if (!list) return;
    const arr: File[] = [];
    for (let i = 0; i < list.length; i++) {
      if (list[i].size > 50 * 1024 * 1024) {
        onToast?.(`${list[i].name}: 50MB 초과 (제외)`, 'error');
        continue;
      }
      arr.push(list[i]);
    }
    setFiles(prev => [...prev, ...arr]);
    if (fileRef.current) fileRef.current.value = '';
  }
  function removeFile(idx: number) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function save() {
    if (!canSave) return;
    setBusy(true);
    try {
      const r = await todosApi.createTodo({
        title: title.trim(),
        description: desc.trim() || undefined,
        assignees,
        due_date: recurOn ? null : (due || null),
        is_template: recurOn,
        recur_type: recurOn ? (recurType === 'none' ? null : recurType) : null,
        recur_days: recurOn && recurType === 'weekly' ? recurDays : null,
        needs_check: !recurOn && toOther && needsCheck,
      });
      if (!r.success || !r.data) {
        onToast?.(r.error || '등록 실패', 'error');
        return;
      }
      const newId = r.data.id;
      // 파일 업로드 (순차)
      let upOk = 0; let upFail = 0;
      for (const f of files) {
        try {
          const ur = await todosApi.uploadAttachment(newId, f);
          if (ur.success) upOk++; else upFail++;
        } catch { upFail++; }
      }
      if (files.length > 0) {
        if (upFail === 0) onToast?.(`등록 완료 (첨부 ${upOk}개)`, 'success');
        else onToast?.(`등록됨 (첨부 ${upOk}/${files.length} 업로드)`, upFail === files.length ? 'error' : 'success');
      } else {
        onToast?.(recurOn ? '반복 일정 등록됨' : '일일 업무 등록됨', 'success');
      }
      onCreated();
      onClose();
    } catch (e) { onToast?.(e instanceof Error ? e.message : '등록 실패', 'error'); }
    finally { setBusy(false); }
  }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.5)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={e => e.stopPropagation()}
        style={{ background: C.white, borderRadius: R.md, maxWidth: 560,
          width: '100%', maxHeight: '90vh', overflow: 'auto', padding: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: T.lg, fontWeight: 800, color: C.text }}>📌 새 일일 업무</div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent',
            fontSize: 22, cursor: 'pointer', color: C.muted, padding: 4 }}>×</button>
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: T.xs, color: C.sub, fontWeight: 600, marginBottom: 5 }}>제목 *</div>
          <input value={title} onChange={e => setTitle(e.target.value)}
            autoFocus placeholder="예: 김대표 자료 검토 요청"
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey && canSave) save(); }}
            style={{ width: '100%', padding: '10px 12px', border: `1px solid ${C.border}`,
              borderRadius: R.sm, fontSize: T.md }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: T.xs, color: C.sub, fontWeight: 600, marginBottom: 5 }}>메모 (선택)</div>
          <textarea value={desc} onChange={e => setDesc(e.target.value)}
            rows={3} placeholder="자세한 지시사항 / 참고 내용"
            style={{ width: '100%', padding: '9px 12px', border: `1px solid ${C.border}`,
              borderRadius: R.sm, fontSize: T.sm, resize: 'vertical', fontFamily: 'inherit' }} />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: T.xs, color: C.sub, fontWeight: 600, marginBottom: 5 }}>담당자 (복수 가능)</div>
          <AssigneeChipPicker value={assignees} staff={staff} myId={myId} onChange={setAssignees} />
        </div>

        {!recurOn && toOther && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, fontSize: T.sm, color: C.text, cursor: 'pointer' }}>
            <input type="checkbox" checked={needsCheck} onChange={e => setNeedsCheck(e.target.checked)} style={{ width: 16, height: 16, margin: 0 }} />
            답 받고 내가 확인하기
            <span style={{ color: C.muted, fontSize: T.xs }}>확인한 뒤 안내까지 해야 끝나요</span>
          </label>
        )}

        {!recurOn && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: T.xs, color: C.sub, fontWeight: 600, marginBottom: 5 }}>마감일</div>
            <DueChipPicker value={due} onChange={setDue} />
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: T.xs, color: C.sub, fontWeight: 600, marginBottom: 5 }}>
            첨부파일 {files.length > 0 && <span style={{ color: C.accent }}>({files.length})</span>}
          </div>
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); addFiles(e.dataTransfer.files); }}
            onClick={() => fileRef.current?.click()}
            style={{
              border: `1.5px dashed ${dragOver ? C.accent : C.border}`,
              background: dragOver ? C.accentLt : C.bgAlt,
              borderRadius: R.sm, padding: '10px 12px',
              textAlign: 'center', cursor: 'pointer',
              marginBottom: files.length > 0 ? 6 : 0,
            }}>
            <div style={{ fontSize: T.sm, color: dragOver ? C.accent : C.sub, fontWeight: 600 }}>
              📎 파일 추가 (클릭 또는 드래그)
            </div>
            <div style={{ fontSize: T.xs, color: C.muted, marginTop: 2 }}>
              최대 50MB / 여러 파일 가능
            </div>
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
              onChange={e => addFiles(e.target.files)}
              onClick={e => e.stopPropagation()} />
          </div>
          {files.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {files.map((f, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8,
                  padding: '5px 9px', background: C.white,
                  border: `1px solid ${C.border}`, borderRadius: R.sm }}>
                  <span style={{ fontSize: T.md }}>📎</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: T.xs, fontWeight: 600, color: C.text,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.name}
                    </div>
                    <div style={{ fontSize: T.xs, color: C.muted }}>{formatSize(f.size)}</div>
                  </div>
                  <button onClick={() => removeFile(idx)}
                    style={{ padding: '3px 8px', borderRadius: R.sm,
                      border: `1px solid ${C.border}`, background: C.white,
                      color: C.red, fontSize: T.xs, cursor: 'pointer' }}>
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 16, padding: 12,
          background: recurOn ? C.accentLt : C.bgAlt,
          border: `1px solid ${recurOn ? C.accent : C.border}`, borderRadius: R.sm }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={recurOn}
              onChange={e => setRecurOn(e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <span style={{ fontSize: T.sm, fontWeight: 700,
              color: recurOn ? C.accent : C.text }}>
              🔁 반복 등록 (지정한 요일마다 매일 00시 자동 생성)
            </span>
          </label>
          {recurOn && (
            <div style={{ marginTop: 10 }}>
              <RecurrencePicker type={recurType} days={recurDays}
                onChange={(t, d) => { setRecurType(t); setRecurDays(d); }} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8,
          paddingTop: 8, borderTop: `1px solid ${C.border}` }}>
          <button onClick={onClose} disabled={busy}
            style={{ padding: '9px 16px', borderRadius: R.sm,
              border: `1px solid ${C.border}`, background: C.white,
              color: C.sub, fontSize: T.sm, cursor: busy ? 'wait' : 'pointer' }}>취소</button>
          <button onClick={save} disabled={!canSave}
            style={{ padding: '9px 22px', borderRadius: R.sm, border: 'none',
              background: canSave ? C.accent : C.muted, color: C.white,
              fontSize: T.sm, fontWeight: 700,
              cursor: canSave ? 'pointer' : 'not-allowed' }}>
            {busy ? '등록중...' : (recurOn ? '🔁 반복 등록' : '등록')}
          </button>
        </div>
      </div>
    </div>
  );
}
